import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Zap, CheckCircle, AlertCircle } from 'lucide-react';

export default function SSOCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleSSOCallback } = useAuth();
  const [status, setStatus] = useState('processing');
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    const provider = searchParams.get('provider');
    const ssoError = searchParams.get('sso_error');

    if (ssoError) {
      setStatus('error');
      const errorMessages = {
        'invalid_state': 'Invalid session state. Please try again.',
        'state_expired': 'Session expired. Please try logging in again.',
        'domain_not_allowed': 'Your email domain is not allowed for this organization.',
        'user_not_found': 'User not found and auto-provisioning is disabled.',
        'account_pending': 'Your account is pending approval.',
        'callback_failed': 'SSO authentication failed. Please try again.',
        'no_email': 'Could not retrieve email from SSO provider.',
        'missing_params': 'Missing required parameters.',
      };
      setError(errorMessages[ssoError] || 'An error occurred during SSO login.');
      return;
    }

    if (token) {
      // Process the SSO token
      try {
        handleSSOCallback(token, provider);
        setStatus('success');
        setTimeout(() => {
          navigate('/dashboard');
        }, 1500);
      } catch (err) {
        setStatus('error');
        setError('Failed to process SSO login.');
      }
    } else {
      setStatus('error');
      setError('No authentication token received.');
    }
  }, [searchParams, navigate, handleSSOCallback]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="max-w-md w-full p-8 bg-white rounded-xl shadow-lg text-center">
        <div className="w-16 h-16 rounded-xl bg-sky-500 flex items-center justify-center mx-auto mb-6">
          <Zap className="w-10 h-10 text-white" />
        </div>

        {status === 'processing' && (
          <>
            <div className="animate-spin w-12 h-12 border-4 border-sky-500 border-t-transparent rounded-full mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Completing Sign In</h2>
            <p className="text-slate-500">Please wait while we verify your credentials...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Sign In Successful</h2>
            <p className="text-slate-500">Redirecting to dashboard...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Sign In Failed</h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={() => navigate('/login')}
              className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
            >
              Back to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
